/**
 * Applies declarative projections to unknown values, retaining only explicitly selected scalar, object, or Map data.
 */
import { isArray, isBoolean, isNumber, isPlainObject, isString, mapValues, pick } from 'remeda';

export type ProjectionFields = {
  readonly [field: string]: true | ProjectionFields;
};

export type Projection<Fields = ProjectionFields> =
  | { mode: 'none' }
  | { mode: 'scalar' }
  | { mode: 'pick'; fields: Fields }
  | { mode: 'pick-map-values'; fields: Fields };

export type ProjectionFieldsFor<Value> = Value extends readonly (infer Item)[]
  ? ProjectionFieldsFor<Item>
  : Value extends object
    ? {
        readonly [Field in keyof Value]?: NonNullable<Value[Field]> extends readonly (infer Item)[]
          ? ProjectionFieldsFor<Item>
          : NonNullable<Value[Field]> extends object
            ? ProjectionFieldsFor<NonNullable<Value[Field]>>
            : true;
      }
    : never;

type ProjectionScalar = string | number | boolean | null;

function isProjectionScalar(value: unknown): value is ProjectionScalar {
  return value === null || isString(value) || isNumber(value) || isBoolean(value);
}

function pickFields(value: unknown, fields: ProjectionFields): unknown {
  if (isArray(value)) return value.map((item) => pickFields(item, fields));
  if (!isPlainObject(value)) return isProjectionScalar(value) ? value : {};

  return mapValues(pick(value as Record<string, unknown>, Object.keys(fields)), (item, field) => {
    const nestedFields = fields[field];
    return nestedFields === true ? (isProjectionScalar(item) ? item : {}) : pickFields(item, nestedFields);
  });
}

export function applyProjection<Fields>(values: unknown[], projection: Projection<Fields>): unknown[] {
  switch (projection.mode) {
    case 'none':
      return [];
    case 'scalar':
      return values.filter(isProjectionScalar);
    case 'pick':
      return values
        .filter((value) => value !== null && typeof value === 'object')
        .map((value) => pickFields(value, projection.fields as ProjectionFields));
    case 'pick-map-values':
      return values
        .filter((value) => value instanceof Map)
        .map((value) =>
          Array.from(value)
            .filter(([key]) => isProjectionScalar(key))
            .map(([key, item]) => ({
              key,
              value:
                item !== null && typeof item === 'object'
                  ? pickFields(item, projection.fields as ProjectionFields)
                  : {},
            })),
        );
  }
}
